// Shared HTTP for /api/ads-assistant/* (JWT + optional X-Workspace-Organization-Id).
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

export const NOTES_BASE = "/api/ads-assistant/notes";
export const BOOKMARKS_BASE = "/api/ads-assistant/bookmarks";
export const SOCIAL_POSTS_BASE = "/api/ads-assistant/social-posts";
export const SOCIAL_POST_MEDIA_BASE = "/api/ads-assistant/social-post-media";
export const POST_ANALYSES_BASE = "/api/ads-assistant/post-analyses";
export const CATEGORIES_BASE = "/api/ads-assistant/categories";
export const TEMPLATES_BASE = "/api/ads-assistant/templates";
export const POLICIES_BASE = "/api/ads-assistant/policies";
export const POST_TAG_DEFS_BASE = "/api/ads-assistant/post-tag-defs";
export const SOCIAL_MAPPINGS_BASE = "/api/ads-assistant/social-mappings";
export const FEED_IMPORT_BASE = "/api/ads-assistant/feed-import";
export const FEED_IMPORT_STATUS_BASE = "/api/ads-assistant/feed-import-status";
export const META_IMPORT_SECRET_BASE = "/api/ads-assistant/meta-import-secret";
export const GOOGLE_IMPORT_SECRET_BASE = "/api/ads-assistant/google-import-secret";
export const ORG_LOCATION_TOOL_LINKS_BASE = "/api/ads-assistant/org-location-tool-links";
export const ORG_LIFECYCLE_BASE = "/api/ads-assistant/org-lifecycle";
export const LIBRARY_ASSIST_BASE = "/api/ads-assistant/library-assist";

export type AdsAssistantFetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string };

export async function adsAssistantClientFetch<T>(path: string, init: RequestInit = {}): Promise<AdsAssistantFetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in to use Ads Assistant." };

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    /* non-JSON */
  }

  if (!r.ok) {
    const err =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: err };
  }

  return { ok: true, status: r.status, data: json as T };
}
